export interface User {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  superuser?: boolean;
}

export interface Organisation {
  id: string;
  name: string;
  slug: string;
}

export interface TermsVersion {
  id: string;
  title: string;
  content: string;
  versionNumber: number;
}

export interface OrganisationMember {
  id: string;
  user: User;
  role: string;
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  invitedBy: User;
  createdAt: string;
}

export interface Client {
  id: string;
  name: string;
  industry?: string;
  website?: string;
  notes?: string;
  contacts: ClientContact[];
  campaigns?: Campaign[];
}

export interface ClientContact {
  id: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  isPrimary: boolean;
}

export interface Campaign {
  id: string;
  title: string;
  status: string;
  currentStage: number;
  candidateCount: number;
  archivedAt?: string | null;
  archiveReason?: string | null;
  client: { id: string; name: string; industry?: string };
  createdBy: { id: string; name: string };
  teamMembers?: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string | null;
}

export interface Candidate {
  id: string;
  referenceNumber?: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  phone?: string;
  linkedinUrl?: string;
  currentRoleTitle?: string;
  currentCompany?: string;
  location?: string;
  notes?: string;
  consentStatus?: string;
  updatedAt?: string;
}

// Mirrors Candidate::CONSENT_STATUSES.
export const CONSENT_STATUSES = [
  "not_requested",
  "pending",
  "given",
  "refused",
  "withdrawn",
] as const;

// Mirrors Types::CandidatePoolSortEnum.
export type CandidatePoolSortBy =
  | "NAME"
  | "CONSENT_STATUS"
  | "LAST_ACTIVITY"
  | "CREATED"
  | "REFERENCE";

export interface CandidatePoolView {
  id: string;
  name: string;
  sortBy?: CandidatePoolSortBy | null;
  sortDirection?: "ASC" | "DESC" | null;
  search?: string | null;
  stages: string[];
  consentStatuses: string[];
  activeSince?: string | null;
}

export interface CampaignCandidate {
  id: string;
  candidate: Candidate;
  stage: string;
  source?: string;
  notes?: string;
  addedBy: { id: string; name: string };
}

export const CAMPAIGN_STATUSES = ["draft", "active", "on_hold", "completed", "cancelled"] as const;

export const CANDIDATE_STAGES = [
  "longlist",
  "outreach",
  "assessment",
  "shortlist",
  "interview",
  "offer",
  "placed",
  "rejected",
  "withdrawn",
] as const;

// Mirrors CampaignCandidate::PIPELINE_STAGES — stages where "Return to pool" is meaningful.
export const PIPELINE_STAGES = [
  "longlist",
  "outreach",
  "assessment",
  "shortlist",
  "interview",
  "offer",
  "placed",
] as const;

// Mirrors CampaignCandidate::TERMINAL_STAGES.
export const TERMINAL_STAGES = ["rejected", "withdrawn"] as const;

export const STAGE_LABELS: Record<string, string> = {
  longlist: "Longlist",
  outreach: "Outreach",
  assessment: "Assessment",
  shortlist: "Shortlist",
  interview: "Interview",
  offer: "Offer",
  placed: "Placed",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

export const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  on_hold: "On Hold",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Chat types

export interface ChatAttachment {
  signed_id: string;
  filename: string;
  content_type: string;
  url?: string;
  size?: number;
  preview?: string;
  uploading?: boolean;
  error?: string;
}

// Rich-text artefact: pasted HTML converted to markdown, surfaced in the
// composer as a removable/expandable pill (mirrors how file attachments
// appear) and persisted on the chat message as a separate content block.
//
// `id` is a client-side identifier used only for React keys and expand-state
// tracking — the backend re-generates one on persist, so don't rely on it
// after a round-trip.
export interface ChatRichTextArtefact {
  id: string;
  label: string;
  size_bytes: number;
  markdown: string;
}

export type ChatThreadPhase =
  | "idle"
  /** Turn enqueued, waiting for a worker to pick it up. */
  | "queued"
  | "thinking"
  | "executing_tool"
  | "responding"
  | "awaiting_plan_approval"
  | "awaiting_step_continuation"
  | "awaiting_user_answer";

export interface ChatThread {
  id: string;
  campaign_id?: string;
  title: string;
  status: "active" | "completed" | "error" | "interrupted";
  current_phase: ChatThreadPhase;
  /** Per-thread monotonic broadcast sequence number; every ChatChannel
   *  payload for this thread carries the seq of the write it announces. */
  broadcast_seq?: number;
  model: string;
  /** False once the org's fixed model has changed since this thread began —
      the thread is kept for history but can no longer be continued. */
  continuable?: boolean;
  plan_mode_enabled?: boolean;
  last_message_at: string | null;
  estimated_cost_cents: number;
  total_tokens: number;
  compacted_at?: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: string;
  thread_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  tool_use?: boolean;
  tool_name?: string;
  tool_result?: boolean;
  compacted?: boolean;
  input_tokens?: number;
  attachments?: ChatAttachment[];
  rich_text_artefacts?: ChatRichTextArtefact[];
  metadata?: Record<string, unknown> | null;
  /** Client-only marker for an optimistic message whose send failed. Absent on
   *  persisted messages; drives the "failed to send" + retry affordance. */
  status?: "failed";
  created_at: string;
}

// Plan-mode chat: a structured proposal the user must approve before any
// tool that writes data may run.

export type ChatPlanState =
  | "proposed"
  | "approved_run_all"
  | "approved_step_by_step"
  | "executing"
  | "step_paused"
  | "completed"
  | "rejected"
  | "cancelled"
  | "superseded";

export type ChatPlanStepStatus = "pending" | "running" | "completed" | "skipped" | "failed";

export interface ChatPlanStep {
  description: string;
  tool_name?: string | null;
  tool_input?: Record<string, unknown> | null;
  status: ChatPlanStepStatus;
  result_message_id?: string | null;
}

export interface ChatPlan {
  id: string;
  thread_id: string;
  state: ChatPlanState;
  execution_mode?: "run_all" | "step_by_step" | null;
  summary: string;
  steps: ChatPlanStep[];
  current_step_index: number;
  rejection_feedback?: string | null;
}

// Agent-maintained task list (Claude Code's TodoWrite semantics). Replaced
// wholesale on each write; surfaced to the user as a checklist.
export type ChatTaskStatus = "pending" | "in_progress" | "completed";

export interface ChatTask {
  content: string;
  status: ChatTaskStatus;
}

export type ChatQuestionKind = "single_select" | "multi_select" | "free_text";
export type ChatQuestionState = "open" | "answered" | "cancelled";

export interface ChatQuestionOption {
  value: string;
  label: string;
  hint?: string;
}

export interface ChatQuestion {
  id: string;
  thread_id: string;
  state: ChatQuestionState;
  kind: ChatQuestionKind;
  prompt: string;
  options: ChatQuestionOption[];
  allow_other: boolean;
  answer?: unknown;
}

export interface ChatDebugData {
  thread: {
    id: string;
    title: string;
    status: string;
    model: string;
    created_at: string;
    current_turn_count: number;
    max_turns_limit: number;
  };
  statistics: {
    total_messages: number;
    user_messages: number;
    assistant_messages: number;
    tool_use_messages: number;
  };
  tokens: {
    total_input_tokens: number;
    total_output_tokens: number;
    total_cache_creation_tokens: number;
    total_cache_read_tokens: number;
    total_tokens: number;
  };
  cost: {
    estimated_cost_cents: number;
    estimated_cost_without_caching_cents: number;
    cache_savings_cents: number;
    cache_efficiency_percent: number;
  };
  messages: ChatDebugMessage[];
}

export interface ChatDebugMessage {
  id: string;
  role: string;
  content: string;
  tool_use: boolean;
  tool_name: string | null;
  tool_input: Record<string, unknown> | null;
  tool_result: boolean;
  tool_use_id: string | null;
  tool_output: unknown;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  api_model: string | null;
  estimated_cost_cents: number;
  model_used: string | null;
  system_prompt: string | null;
  metadata: Record<string, unknown> | null;
  wire_payload: ChatDebugWirePayload | null;
  created_at: string;
}

// Render-time PII token resolution for a chat thread (story #2324):
// persisted tool results carry C{n} / [LABEL_n] tokens; the UI resolves
// them to display values via /chat/threads/:id/token_resolutions.
export interface TokenCandidateResolution {
  id: string;
  full_name: string;
  path: string;
}

export interface TokenResolutions {
  generic: Record<string, string>;
  candidates: Record<string, TokenCandidateResolution>;
}

export interface ChatDebugWirePayload {
  id: string;
  model: string;
  provider: string;
  clean: boolean;
  redaction_counts: { candidate?: number; generic?: number } & Record<string, number>;
  suspected_pii_findings: {
    candidate?: number;
    generic?: number;
    generic_labels?: string[];
  };
  system: unknown;
  messages: unknown;
  tools: unknown;
  created_at: string;
}
